/**
 * Control de cobranza operativa — tablero mensual Honorarios
 */
import { useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ChevronDown, Download, ExternalLink, FileUp, Loader2, Mail, MessageSquare, RefreshCw } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import {
  DespachoKpiCard,
  DespachoSectionCard,
  DespachoSimpleTable,
  DespachoStatusBadge,
} from "@/components/despacho/DespachoUi";
import {
  collectionStatusLabel,
  honorariosApi,
  parseHonorariosError,
} from "@/modules/despacho/despachoHonorariosApi";
import {
  BILLING_CONDITION_LABELS,
  COLLECTION_EMAIL_STATUS_LABELS,
  FISCAL_STATUS_LABELS,
  OPERATIONAL_STATUS_LABELS,
  REMINDER_STATUS_LABELS,
} from "@/modules/despacho/honorariosBoardLabels";
import { goToSatVisorForUuid } from "@/lib/satVisorNavigation";
import { emailTransportStatusLabel, smtpPendingHint } from "@/lib/smtpPresets";
import { despachoMensajesGeneralesApi } from "@/modules/despacho/despachoMensajesGeneralesApi";

export {
  BILLING_CONDITION_LABELS,
  FISCAL_STATUS_LABELS,
  OPERATIONAL_STATUS_LABELS,
  REMINDER_STATUS_LABELS,
} from "@/modules/despacho/honorariosBoardLabels";

const MES_FULL = [
  "Enero", "Febrero", "Marzo", "Abril", "Mayo", "Junio",
  "Julio", "Agosto", "Septiembre", "Octubre", "Noviembre", "Diciembre",
];

function lastDayOfWorkMonth(year, month) {
  const y = Number(year);
  const m = Number(month);
  const day = new Date(y, m, 0).getDate();
  return `${String(y)}-${String(m).padStart(2, "0")}-${String(day).padStart(2, "0")}`;
}

function firstDayOfWorkMonth(year, month) {
  const y = Number(year);
  const m = Number(month);
  return `${String(y)}-${String(m).padStart(2, "0")}-01`;
}

/** mes = solo mes/año del tablero; periodo = rango Desde–Hasta (validación por emisión). */
export const DATE_FILTER_MODE_MES = "mes";
export const DATE_FILTER_MODE_PERIODO = "periodo";

function formatDateLabel(iso) {
  const raw = String(iso || "").slice(0, 10);
  if (!raw || raw.length < 10) return "";
  return raw.split("-").reverse().join("/");
}

/** ISO YYYY-MM-DD → texto dd/mm/aaaa para inputs visibles. */
function isoToDmy(iso) {
  const raw = String(iso || "").slice(0, 10);
  if (!/^\d{4}-\d{2}-\d{2}$/.test(raw)) return "";
  const [y, m, d] = raw.split("-");
  return `${d}/${m}/${y}`;
}

/** Texto dd/mm/aaaa (o d/m/aaaa) → ISO YYYY-MM-DD; vacío si inválido. */
function dmyToIso(text) {
  const raw = String(text || "").trim();
  if (!raw) return "";
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) return raw;
  const m = raw.match(/^(\d{1,2})[\/\-.](\d{1,2})[\/\-.](\d{4})$/);
  if (!m) return "";
  const d = Number(m[1]);
  const mo = Number(m[2]);
  const y = Number(m[3]);
  if (mo < 1 || mo > 12 || d < 1 || d > 31) return "";
  return `${String(y).padStart(4, "0")}-${String(mo).padStart(2, "0")}-${String(d).padStart(2, "0")}`;
}

const COBRANZA_CHIPS = [
  { key: "pending", label: "Pendientes", view: "pending" },
  { key: "email", label: "Envío de correo", view: "email" },
  { key: "reminder", label: "Recordatorios", view: "reminder" },
  { key: "collected", label: "Cobradas", view: "collected" },
  { key: "all", label: "Todos", view: "all" },
];

/** Tipos de cliente bajo Control de cobranza (acordeones). */
export const CONDITION_CLIENT_TABS = [
  {
    key: "factura",
    label: "Clientes con factura CFDI",
    billing_condition: "factura",
    description: "Honorarios recurrentes facturados con CFDI.",
  },
  {
    key: "otros_pagos",
    label: "Clientes otros conceptos",
    billing_condition: "otros_pagos",
    description: "Cobros sin honorario recurrente con CFDI (extras / conceptos puntuales).",
  },
  {
    key: "no_recurrente",
    label: "Clientes eventuales",
    billing_condition: "no_recurrente",
    description: "Trabajos eventuales del despacho contable.",
  },
  {
    key: "convenio",
    label: "Convenios (grupos)",
    billing_condition: "convenio",
    description: "Varias empresas; se factura a una y el resto sin cobro.",
  },
  {
    key: "cortesia",
    label: "Clientes con cortesía",
    billing_condition: "cortesia",
    description: "Sin cobro (cortesía).",
  },
  {
    key: "eventual_act_empresarial",
    label: "Eventuales act. empresarial",
    billing_condition: "eventual_act_empresarial",
    description: "Clientes eventuales de actividad empresarial.",
  },
];

const CONDITION_EDIT_OPTIONS = [
  { value: "factura", label: "Clientes con factura CFDI" },
  { value: "otros_pagos", label: "Clientes otros conceptos" },
  { value: "no_recurrente", label: "Clientes eventuales" },
  { value: "convenio", label: "Convenios (grupos)" },
  { value: "cortesia", label: "Clientes con cortesía" },
  { value: "eventual_act_empresarial", label: "Eventuales act. empresarial" },
];

function ConditionAccordionSection({
  title,
  description,
  count,
  isOpen,
  onToggle,
  children,
  testId,
}) {
  const countLabel = count == null ? "" : ` (${count})`;
  return (
    <section
      className={`rounded-sm border bg-white ${isOpen ? "border-[#0F2D5C]/40 shadow-sm" : "border-slate-200"}`}
      data-testid={testId}
    >
      <button
        type="button"
        className="flex w-full items-start justify-between gap-3 px-4 py-3 text-left hover:bg-slate-50"
        aria-expanded={isOpen}
        onClick={onToggle}
        data-testid={testId ? `${testId}-toggle` : undefined}
      >
        <span className="min-w-0">
          <span className="block text-sm font-bold text-[#0F2D5C]">
            {title}
            {countLabel}
          </span>
          {description ? (
            <span className="block text-xs text-slate-500 mt-1">{description}</span>
          ) : null}
        </span>
        <ChevronDown
          className={`mt-0.5 h-4 w-4 shrink-0 text-slate-500 transition-transform ${isOpen ? "rotate-180" : ""}`}
          aria-hidden
        />
      </button>
      {isOpen ? (
        <div className="border-t border-slate-100 p-4 space-y-3">{children}</div>
      ) : null}
    </section>
  );
}

function currentPeriod() {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}`;
}

function splitPeriod(period) {
  const [y, m] = (period || currentPeriod()).split("-");
  return { year: y, month: m };
}

function joinPeriod(year, month) {
  if (!year || !month) return currentPeriod();
  return `${year}-${month.padStart(2, "0")}`;
}

function formatMoney(value, canView) {
  if (!canView || value == null || Number.isNaN(Number(value))) return "—";
  return Number(value).toLocaleString("es-MX", { style: "currency", currency: "MXN" });
}

function formatIssueDate(value) {
  const raw = String(value || "").slice(0, 10);
  if (!raw || raw.length < 10) return "—";
  const [y, m, d] = raw.split("-");
  if (!y || !m || !d) return raw;
  return `${d}/${m}/${y}`;
}

function downloadBlobResponse(response, fallbackName) {
  const blob = response.data;
  if (blob && typeof blob === "object" && typeof blob.type === "string" && blob.type.includes("application/json")) {
    // Error JSON con responseType blob
    return Promise.reject(new Error("No se pudo generar el archivo."));
  }
  const url = window.URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  const cd = response.headers?.["content-disposition"] || "";
  const match = /filename="([^"]+)"/i.exec(cd);
  link.download = match?.[1] || fallbackName;
  document.body.appendChild(link);
  link.click();
  link.remove();
  window.URL.revokeObjectURL(url);
  return Promise.resolve();
}

async function readBlobErrorMessage(err) {
  const data = err?.response?.data;
  if (data instanceof Blob) {
    try {
      const text = await data.text();
      const parsed = JSON.parse(text);
      const detail = parsed?.detail;
      if (typeof detail === "string" && detail.trim()) return detail.trim();
      if (detail && typeof detail === "object" && detail.message) return String(detail.message);
    } catch {
      /* ignore */
    }
  }
  return parseHonorariosError(err).message;
}

export default function HonorariosMonthlyBoard({
  readOnly,
  canViewAmounts,
  canSync,
  canExport,
  canGenerateMessages,
  canConfirmPayment = false,
  canCreateHonorariosTasks = false,
  syncDateFrom,
  onSyncDateFromChange,
  syncDateTo,
  onSyncDateToChange,
  onSyncInvoices,
  syncBusy,
  viewMode = "cobranza",
  messageTemplates = [],
  taskKinds = [],
  defaultChip = "pending",
  /** Controlado desde el shell: mes de trabajo no se pierde al cambiar de pestaña. */
  periodYear: periodYearProp,
  periodMonth: periodMonthProp,
  onPeriodYearChange,
  onPeriodMonthChange,
}) {
  const navigate = useNavigate();
  const init = splitPeriod(currentPeriod());
  const [periodYearLocal, setPeriodYearLocal] = useState(init.year);
  const [periodMonthLocal, setPeriodMonthLocal] = useState(init.month);
  const periodControlled = periodYearProp != null && periodMonthProp != null
    && typeof onPeriodYearChange === "function"
    && typeof onPeriodMonthChange === "function";
  const periodYear = periodControlled ? periodYearProp : periodYearLocal;
  const periodMonth = periodControlled ? periodMonthProp : periodMonthLocal;
  const setPeriodYear = periodControlled ? onPeriodYearChange : setPeriodYearLocal;
  const setPeriodMonth = periodControlled ? onPeriodMonthChange : setPeriodMonthLocal;
  const [activeChip, setActiveChip] = useState(defaultChip);
  /** Vacío = todas las tarjetas cerradas al entrar. */
  const [openConditionKey, setOpenConditionKey] = useState("");
  const [conditionTab, setConditionTab] = useState("factura");
  const [conditionCounts, setConditionCounts] = useState({});
  const [rows, setRows] = useState([]);
  const [summary, setSummary] = useState({});
  const [loadState, setLoadState] = useState("idle");
  const [boardBusy, setBoardBusy] = useState(false);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailData, setDetailData] = useState(null);
  const [detailBusy, setDetailBusy] = useState(false);
  const [patchComment, setPatchComment] = useState("");
  const [conditionDraft, setConditionDraft] = useState("");
  const [rowBusyId, setRowBusyId] = useState("");
  const [proofBusy, setProofBusy] = useState(false);
  const [proofFile, setProofFile] = useState(null);
  const [taskBusy, setTaskBusy] = useState(false);
  const [selectedRowIds, setSelectedRowIds] = useState(() => new Set());
  const [reminderBulkBusy, setReminderBulkBusy] = useState(false);
  const [emailBulkBusy, setEmailBulkBusy] = useState(false);
  const [emailConfig, setEmailConfig] = useState(null);
  const [emailConfigState, setEmailConfigState] = useState("idle");
  const [mailTpl, setMailTpl] = useState({
    subject: "",
    body: "",
    attach_cfdi_pdf: true,
    attach_cfdi_xml: true,
  });
  const [mailTplState, setMailTplState] = useState("idle");
  const [mailTplSaving, setMailTplSaving] = useState(false);
  const [exportBusy, setExportBusy] = useState(false);
  /** Evita mostrar Desde/Hasta y Mes a la vez: el usuario elige un modo. */
  const [dateFilterMode, setDateFilterMode] = useState(DATE_FILTER_MODE_MES);

  const isCortesias = viewMode === "cortesias";
  const period = useMemo(() => joinPeriod(periodYear, periodMonth), [periodYear, periodMonth]);
  const monthDateFrom = useMemo(
    () => firstDayOfWorkMonth(periodYear, periodMonth),
    [periodYear, periodMonth],
  );
  const monthDateTo = useMemo(
    () => lastDayOfWorkMonth(periodYear, periodMonth),
    [periodYear, periodMonth],
  );
  const effectiveDateTo = useMemo(
    () => (syncDateTo && String(syncDateTo).slice(0, 10)) || monthDateTo,
    [syncDateTo, monthDateTo],
  );
  /** Rango efectivo según modo (para sync, listados y reportes). */
  const rangeDateFrom = useMemo(() => {
    if (dateFilterMode === DATE_FILTER_MODE_MES) return monthDateFrom;
    const fromIso = dmyToIso(syncDateFrom)
      || (/^\d{4}-\d{2}-\d{2}$/.test(syncDateFrom || "") ? syncDateFrom : "");
    return fromIso || monthDateFrom;
  }, [dateFilterMode, monthDateFrom, syncDateFrom]);
  const rangeDateTo = useMemo(() => {
    if (dateFilterMode === DATE_FILTER_MODE_MES) return monthDateTo;
    return effectiveDateTo;
  }, [dateFilterMode, monthDateTo, effectiveDateTo]);

  // En modo mes: alinear fechas padre al mes del tablero (sync/API coherentes).
  useEffect(() => {
    if (dateFilterMode !== DATE_FILTER_MODE_MES) return undefined;
    onSyncDateFromChange?.(monthDateFrom);
    onSyncDateToChange?.(monthDateTo);
    return undefined;
  }, [dateFilterMode, monthDateFrom, monthDateTo]); // eslint-disable-line react-hooks/exhaustive-deps

  // En modo periodo: si «Hasta» vacío, default al fin del mes de trabajo.
  useEffect(() => {
    if (dateFilterMode !== DATE_FILTER_MODE_PERIODO) return undefined;
    if (!syncDateTo) onSyncDateToChange?.(monthDateTo);
    return undefined;
  }, [dateFilterMode, periodYear, periodMonth]); // eslint-disable-line react-hooks/exhaustive-deps

  const activeCondition = useMemo(
    () => CONDITION_CLIENT_TABS.find((t) => t.key === conditionTab) || CONDITION_CLIENT_TABS[0],
    [conditionTab],
  );
  const boardEnabled = isCortesias || Boolean(openConditionKey);

  const boardParams = useMemo(() => {
    const params = { period };
    if (isCortesias) {
      params.view = "cortesia";
      return params;
    }
    const cond = activeCondition?.billing_condition;
    if (cond) params.billing_condition = cond;
    // En cortesía los chips operativos no aplican (no hay «pendiente de cobro»)
    if (cond === "cortesia") {
      params.view = "all";
      return params;
    }
    const chip = COBRANZA_CHIPS.find((c) => c.key === activeChip);
    if (chip?.view) params.view = chip.view;
    if (
      (chip?.view === "pending" || chip?.view === "email" || chip?.view === "collected")
      && rangeDateFrom
    ) {
      const fromIso = dmyToIso(rangeDateFrom) || (/^\d{4}-\d{2}-\d{2}$/.test(rangeDateFrom) ? rangeDateFrom : "");
      const toIso = dmyToIso(rangeDateTo) || (/^\d{4}-\d{2}-\d{2}$/.test(rangeDateTo || "") ? rangeDateTo : "");
      if (fromIso) params.issue_date_from = fromIso;
      if (toIso) params.issue_date_to = toIso;
    }
    return params;
  }, [period, activeChip, isCortesias, rangeDateFrom, rangeDateTo, activeCondition]);

  const pendingRangeLabel = useMemo(() => {
    if (
      (activeChip !== "pending" && activeChip !== "email" && activeChip !== "collected")
      || !rangeDateFrom
    ) {
      return "";
    }
    const prefix = activeChip === "collected" ? "Cobradas" : "Pendientes";
    if (dateFilterMode === DATE_FILTER_MODE_MES) {
      return `${prefix} del mes ${MES_FULL[Number(periodMonth) - 1]} ${periodYear}`;
    }
    return `${prefix} del ${formatDateLabel(rangeDateFrom)} al ${formatDateLabel(rangeDateTo)}`;
  }, [activeChip, rangeDateFrom, rangeDateTo, dateFilterMode, periodMonth, periodYear]);

  const loadEmailConfig = useCallback(async () => {
    setEmailConfigState("loading");
    setMailTplState("loading");
    try {
      const cfg = await despachoMensajesGeneralesApi.getEmailConfig();
      setEmailConfig(cfg || {});
      setEmailConfigState("ready");
    } catch {
      setEmailConfig(null);
      setEmailConfigState("error");
    }
    try {
      const tpl = await honorariosApi.getCollectionEmailTemplate();
      setMailTpl({
        subject: tpl?.subject || "",
        body: tpl?.body || "",
        attach_cfdi_pdf: tpl?.attach_cfdi_pdf !== false,
        attach_cfdi_xml: tpl?.attach_cfdi_xml !== false,
      });
      setMailTplState("ready");
    } catch {
      setMailTplState("error");
    }
  }, []);

  useEffect(() => {
    if (activeChip === "email" && !isCortesias) {
      loadEmailConfig();
    }
  }, [activeChip, isCortesias, loadEmailConfig]);

  useEffect(() => {
    setSelectedRowIds(new Set());
  }, [activeChip, openConditionKey]);

  const emailValidation = useMemo(() => {
    const unpaid = rows.filter((r) => r.needs_collection_email ?? !r.manual_collected);
    const ready = unpaid.filter((r) => r.email_ready || r.client_email);
    const missing = unpaid.filter((r) => !(r.email_ready || r.client_email));
    const alreadySent = unpaid.filter((r) => r.collection_email_status === "sent");
    const transportReady = Boolean(emailConfig?.enabled && emailConfig?.transport_ready);
    return {
      unpaidCount: unpaid.length,
      readyCount: ready.length,
      missingCount: missing.length,
      alreadySentCount: alreadySent.length,
      transportReady,
      transportHint: smtpPendingHint(emailConfig) || emailTransportStatusLabel(emailConfig) || "",
    };
  }, [rows, emailConfig]);

  const loadConditionCounts = useCallback(async () => {
    if (isCortesias) return;
    try {
      await honorariosApi.syncPeriodBoardFromRoster({ period });
    } catch {
      /* no bloquear el tablero si falla el sync */
    }
    const entries = await Promise.all(
      CONDITION_CLIENT_TABS.map(async (tab) => {
        try {
          const data = await honorariosApi.getPeriodBoard({
            period,
            billing_condition: tab.billing_condition,
            view: "all",
          });
          return [tab.key, (data.items || []).length];
        } catch {
          return [tab.key, 0];
        }
      }),
    );
    setConditionCounts(Object.fromEntries(entries));
  }, [period, isCortesias]);

  const loadBoard = useCallback(async () => {
    if (!boardEnabled) {
      setRows([]);
      setSummary({});
      setSelectedRowIds(new Set());
      setLoadState("idle");
      return;
    }
    setLoadState("loading");
    try {
      const data = await honorariosApi.getPeriodBoard(boardParams);
      setRows(data.items || []);
      setSummary(data.summary || {});
      setSelectedRowIds(new Set());
      setLoadState("ready");
    } catch (err) {
      setLoadState("error");
      toast.error(parseHonorariosError(err).message);
    }
  }, [boardParams, boardEnabled]);

  useEffect(() => {
    loadBoard();
  }, [loadBoard]);

  useEffect(() => {
    loadConditionCounts();
  }, [loadConditionCounts]);

  useEffect(() => {
    setActiveChip(defaultChip);
  }, [viewMode, defaultChip]);

  useEffect(() => {
    // Al cambiar de mes, cerrar tarjetas (mismo patrón que Calendario / captura).
    setOpenConditionKey("");
  }, [period]);

  const toggleConditionAccordion = (key) => {
    if (openConditionKey === key) {
      setOpenConditionKey("");
      return;
    }
    setConditionTab(key);
    setActiveChip(defaultChip);
    setOpenConditionKey(key);
  };

  const handleGenerateMonth = async () => {
    if (readOnly || !canSync) return;
    setBoardBusy(true);
    try {
      const data = await honorariosApi.generatePeriodBoard(period);
      toast.success(`Mes generado: ${data.summary?.created ?? 0} nuevas, ${data.summary?.updated ?? 0} actualizadas.`);
      await loadConditionCounts();
      await loadBoard();
    } catch (err) {
      toast.error(parseHonorariosError(err).message);
    } finally {
      setBoardBusy(false);
    }
  };

  const handleRefreshPayments = async () => {
    if (readOnly || !canSync) return;
    setBoardBusy(true);
    try {
      const data = await honorariosApi.refreshPayments({ period, issue_date_from: rangeDateFrom || undefined });
      const inv = data.invoices || {};
      toast.success(`Cobros recalculados: ${inv.updated ?? 0} factura(s).`);
      await loadBoard();
    } catch (err) {
      toast.error(parseHonorariosError(err).message);
    } finally {
      setBoardBusy(false);
    }
  };

  const handleToggleManual = async (row, checked) => {
    if (readOnly || rowBusyId) return;
    setRowBusyId(row.id);
    try {
      await honorariosApi.patchPeriodBoardRow(row.id, { manual_collected: checked });
      toast.success(
        checked
          ? "Marcado cobrado. Si hay CFDI, se actualizó también el estado de la factura."
          : "Marcado pendiente en el tablero.",
      );
      await loadBoard();
    } catch (err) {
      toast.error(parseHonorariosError(err).message);
    } finally {
      setRowBusyId("");
    }
  };

  const handleExport = async (mode = "view") => {
    if (!canExport) return;
    setExportBusy(true);
    try {
      const cond = !isCortesias ? activeCondition?.billing_condition : undefined;
      let view;
      let format = "csv";
      let dateFrom;
      let dateTo;
      if (mode === "pending" || mode === "collected") {
        view = mode;
        format = "xlsx";
        dateFrom = rangeDateFrom || undefined;
        dateTo = rangeDateTo || undefined;
      } else if (isCortesias) {
        view = "cortesia";
      } else if (cond === "cortesia") {
        view = "all";
      } else {
        view = COBRANZA_CHIPS.find((c) => c.key === activeChip)?.view || "reminder";
        if (view === "pending" || view === "email" || view === "collected") {
          dateFrom = rangeDateFrom || undefined;
          dateTo = rangeDateTo || undefined;
        }
      }
      const response = await honorariosApi.exportPeriodBoard({
        period,
        view,
        format,
        ...(cond && cond !== "cortesia" ? { billing_condition: cond } : {}),
        ...(dateFrom ? { issue_date_from: dateFrom } : {}),
        ...(dateTo ? { issue_date_to: dateTo } : {}),
      });
      const ext = format === "xlsx" ? "xlsx" : "csv";
      const slug = mode === "pending"
        ? "por_cobrar"
        : mode === "collected"
          ? "cobradas"
          : (cond || view);
      const rangeSlug = dateFrom && dateTo
        ? `_${String(dateFrom).replace(/-/g, "")}_${String(dateTo).replace(/-/g, "")}`
        : `_${period}`;
      downloadBlobResponse(response, `cobranza_${slug}${rangeSlug}.${ext}`);
      toast.success(format === "xlsx" ? "Excel descargado." : "CSV descargado.");
    } catch (err) {
      toast.error(await readBlobErrorMessage(err));
    } finally {
      setExportBusy(false);
    }
  };

  const toggleRowSelected = (rowId, checked) => {
    setSelectedRowIds((prev) => {
      const next = new Set(prev);
      if (checked) next.add(rowId);
      else next.delete(rowId);
      return next;
    });
  };

  const selectableUnpaidRows = useMemo(
    () => rows.filter((r) => (r.needs_collection_email ?? r.needs_reminder) && r.billing_condition !== "cortesia"),
    [rows],
  );

  const selectableReminderRows = useMemo(
    () => rows.filter((r) => r.needs_reminder && r.billing_condition !== "cortesia"),
    [rows],
  );

  const allUnpaidSelected = Boolean(
    selectableUnpaidRows.length
    && selectableUnpaidRows.every((r) => selectedRowIds.has(r.id)),
  );

  const toggleSelectAllUnpaid = (checked) => {
    if (!checked) {
      setSelectedRowIds(new Set());
      return;
    }
    setSelectedRowIds(new Set(selectableUnpaidRows.map((r) => r.id)));
  };

  const handleBulkReminders = async (useSelection) => {
    if (readOnly || !canGenerateMessages) return;
    const ids = useSelection
      ? Array.from(selectedRowIds)
      : selectableReminderRows.map((r) => r.id);
    if (!ids.length) {
      toast.error("Seleccione al menos un pendiente o use «Todos los pendientes».");
      return;
    }
    setReminderBulkBusy(true);
    try {
      const data = await honorariosApi.generatePeriodBoardRemindersBulk({ rowIds: ids });
      const created = data.created ?? 0;
      const skipped = data.skipped ?? 0;
      if (created) {
        toast.success(
          `Se prepararon ${created} borrador(es) para WhatsApp${skipped ? ` · ${skipped} omitido(s)` : ""}.`,
        );
      } else {
        toast.message(`Sin borradores nuevos (${skipped} omitido(s); puede que ya existan).`);
      }
      await loadBoard();
    } catch (err) {
      toast.error(parseHonorariosError(err).message);
    } finally {
      setReminderBulkBusy(false);
    }
  };

  const handleBulkCollectionEmails = async (useSelection) => {
    if (readOnly || !canGenerateMessages) return;
    if (emailConfig && !emailValidation.transportReady) {
      toast.error("Configure Parámetros de correo antes de enviar.", {
        description: emailValidation.transportHint || undefined,
      });
      return;
    }
    const ids = useSelection
      ? Array.from(selectedRowIds)
      : selectableUnpaidRows
        .filter((r) => r.email_ready || r.client_email)
        .map((r) => r.id);
    if (!ids.length) {
      toast.error("Seleccione clientes no cobrados con correo en ficha.");
      return;
    }
    setEmailBulkBusy(true);
    try {
      const data = await honorariosApi.sendPeriodBoardCollectionEmailsBulk({ rowIds: ids });
      const sent = data.sent ?? 0;
      const skipped = data.skipped ?? 0;
      const failed = data.failed ?? 0;
      if (sent) {
        toast.success(
          `Se enviaron ${sent} correo(s) a clientes no cobrados`
            + `${failed ? ` · ${failed} con error` : ""}`
            + `${skipped ? ` · ${skipped} omitido(s)` : ""}.`,
        );
      } else if (failed) {
        const firstErr = data.errors?.[0]?.detail;
        toast.error(firstErr || `No se pudo enviar (${failed} fallido(s), ${skipped} omitido(s)).`);
      } else {
        toast.message(`Sin correos enviados (${skipped} omitido(s); ya enviados o sin correo en ficha).`);
      }
      await loadBoard();
    } catch (err) {
      toast.error(parseHonorariosError(err).message);
    } finally {
      setEmailBulkBusy(false);
    }
  };

  const handleSaveMailTemplate = async () => {
    if (readOnly || !canGenerateMessages) return;
    setMailTplSaving(true);
    try {
      const saved = await honorariosApi.saveCollectionEmailTemplate({
        subject: mailTpl.subject,
        body: mailTpl.body,
        attachCfdiPdf: mailTpl.attach_cfdi_pdf,
        attachCfdiXml: mailTpl.attach_cfdi_xml,
      });
      setMailTpl({
        subject: saved?.subject || mailTpl.subject,
        body: saved?.body || mailTpl.body,
        attach_cfdi_pdf: saved?.attach_cfdi_pdf !== false,
        attach_cfdi_xml: saved?.attach_cfdi_xml !== false,
      });
      toast.success("Plantilla de correo guardada (se usará en los siguientes envíos).");
    } catch (err) {
      toast.error(parseHonorariosError(err).message);
    } finally {
      setMailTplSaving(false);
    }
  };

  const handleSendCollectionEmail = async (row) => {
    if (readOnly || !canGenerateMessages) return;
    setRowBusyId(row.id);
    try {
      const data = await honorariosApi.sendPeriodBoardCollectionEmail(row.id);
      if (data.ok) {
        toast.success(`Correo enviado a ${(data.to_emails || []).join(", ") || "el cliente"}.`);
      } else {
        toast.error(data.detail || "No se pudo enviar el correo.");
      }
      await loadBoard();
    } catch (err) {
      toast.error(parseHonorariosError(err).message);
    } finally {
      setRowBusyId("");
    }
  };

  const openDetail = async (rowId) => {
    setDetailOpen(true);
    setDetailBusy(true);
    setPatchComment("");
    try {
      const data = await honorariosApi.getPeriodBoardRow(rowId);
      setDetailData(data);
      setConditionDraft(data?.item?.billing_condition || "");
      setProofFile(null);
    } catch (err) {
      toast.error(parseHonorariosError(err).message);
      setDetailOpen(false);
    } finally {
      setDetailBusy(false);
    }
  };

  const handlePatchRow = async (patch) => {
    if (readOnly || !detailData?.item?.id) return;
    try {
      const updated = await honorariosApi.patchPeriodBoardRow(detailData.item.id, patch);
      const nextCond = updated?.item?.billing_condition || patch.billing_condition;
      toast.success("Fila actualizada.");
      if (nextCond && nextCond !== conditionTab && !isCortesias) {
        const dest = CONDITION_CLIENT_TABS.find(
          (t) => t.key === nextCond || t.billing_condition === nextCond,
        );
        toast.message(dest ? `Cliente movido a «${dest.label}».` : "Condición actualizada.");
        setDetailOpen(false);
        const destKey = dest?.key || (nextCond === "eventual" ? "no_recurrente" : nextCond);
        setConditionTab(destKey);
        setOpenConditionKey(destKey);
      } else {
        await openDetail(detailData.item.id);
      }
      await loadConditionCounts();
      await loadBoard();
    } catch (err) {
      toast.error(parseHonorariosError(err).message);
    }
  };

  const handleChangeCondition = async () => {
    if (readOnly || !detailData?.item?.id) return;
    const next = String(conditionDraft || "").trim();
    const current = detailData.item.billing_condition;
    if (!next || next === current) {
      toast.error("Seleccione una condición distinta.");
      return;
    }
    if (!String(patchComment || "").trim()) {
      toast.error("Indique un comentario al cambiar la condición.");
      return;
    }
    await handlePatchRow({
      billing_condition: next,
      comment: patchComment.trim(),
    });
  };

  const handleOpenCfdiVisor = async () => {
    const inv = detailData?.invoice || {};
    const row = detailData?.item || {};
    const uuid = inv.cfdi_uuid || "";
    const clientId = row.fiscal_client_id || inv.fiscal_client_id || "";
    if (!uuid) {
      toast.error("Sin UUID de CFDI vinculado.");
      return;
    }
    try {
      await goToSatVisorForUuid(navigate, clientId, uuid);
    } catch (err) {
      toast.error(parseHonorariosError(err).message || "No se pudo abrir el visor.");
    }
  };

  const handleConfirmFiscalPaid = async () => {
    const inv = detailData?.invoice;
    const row = detailData?.item;
    if (readOnly || !canConfirmPayment || !inv?.id || !row?.id) return;
    try {
      await honorariosApi.patchCollectionStatus(inv.id, {
        collection_status: "paid_confirmed",
        comment: "Confirmación fiscal desde Control de cobranza",
      });
      if (!row.manual_collected) {
        await honorariosApi.patchPeriodBoardRow(row.id, { manual_collected: true });
      }
      toast.success("Pago confirmado en factura y tablero.");
      await openDetail(row.id);
      await loadBoard();
    } catch (err) {
      toast.error(parseHonorariosError(err).message);
    }
  };

  const handleCreateTaskFromDetail = async (kindId) => {
    const inv = detailData?.invoice;
    const row = detailData?.item;
    if (readOnly || !canCreateHonorariosTasks || !row?.fiscal_client_id) return;
    setTaskBusy(true);
    try {
      await honorariosApi.createHonorariosTask({
        fiscal_client_id: row.fiscal_client_id,
        honorarios_invoice_id: inv?.id || undefined,
        honorarios_task_kind: kindId,
      });
      toast.success("Tarea de cobranza creada.");
    } catch (err) {
      toast.error(parseHonorariosError(err).message);
    } finally {
      setTaskBusy(false);
    }
  };

  const handleUploadProofFromDetail = async (event) => {
    event.preventDefault();
    const row = detailData?.item;
    if (readOnly || !row?.fiscal_client_id || !proofFile) return;
    setProofBusy(true);
    try {
      const fd = new FormData();
      fd.append("file", proofFile);
      fd.append("fiscal_client_id", row.fiscal_client_id);
      if (detailData?.invoice?.id) fd.append("honorarios_invoice_id", detailData.invoice.id);
      await honorariosApi.uploadPaymentProof(fd);
      toast.success("Comprobante cargado.");
      setProofFile(null);
      await openDetail(row.id);
    } catch (err) {
      toast.error(parseHonorariosError(err).message);
    } finally {
      setProofBusy(false);
    }
  };

  const yearOptions = useMemo(() => {
    const end = new Date().getFullYear() + 1;
    const years = [];
    for (let y = end; y >= 2024; y -= 1) years.push(y);
    return years;
  }, []);

  const columns = useMemo(() => {
    if (isCortesias) {
      return [
        { key: "client_name", label: "Cliente", render: (r) => r.client_name || "—" },
        { key: "period", label: "Periodo", render: (r) => r.period || "—" },
        { key: "notes", label: "Observación", render: (r) => r.notes || "—" },
        {
          key: "_actions",
          label: "",
          render: (r) => (
            <Button type="button" variant="ghost" size="sm" className="h-7" onClick={() => openDetail(r.id)}>
              Ver
            </Button>
          ),
        },
      ];
    }

    const cols = [];
    const showEmailUi = activeChip === "email";
    const showSelect = canGenerateMessages && !readOnly && (showEmailUi || activeChip === "reminder");

    if (showSelect) {
      cols.push({
        key: "_select",
        label: (
          <Checkbox
            checked={allUnpaidSelected}
            disabled={!selectableUnpaidRows.length}
            onCheckedChange={(v) => toggleSelectAllUnpaid(v === true)}
            aria-label="Seleccionar todos los no cobrados"
            data-testid="honorarios-unpaid-select-all"
          />
        ),
        render: (r) => (
          (r.needs_collection_email ?? r.needs_reminder) ? (
            <Checkbox
              checked={selectedRowIds.has(r.id)}
              onCheckedChange={(v) => toggleRowSelected(r.id, v === true)}
              aria-label={`Seleccionar ${r.client_name || "fila"}`}
              data-testid={`honorarios-unpaid-select-${r.id}`}
            />
          ) : null
        ),
      });
    }
    cols.push(
      { key: "client_name", label: "Cliente", render: (r) => r.client_name || "—" },
      { key: "invoice_label", label: "Factura / Mes", render: (r) => r.invoice_label || "—" },
    );
    if (showEmailUi) {
      cols.push({
        key: "client_email",
        label: "Correo ficha",
        render: (r) => (
          r.client_email
            ? <span className="text-xs text-slate-700">{r.client_email}</span>
            : <span className="text-xs text-amber-700">Sin correo</span>
        ),
      });
    } else {
      cols.push({
        key: "invoice_issue_date",
        label: "Fecha factura",
        render: (r) => formatIssueDate(r.invoice_issue_date),
      });
    }
    if (activeChip === "pending" || activeChip === "email") {
      cols.push({
        key: "period",
        label: "Mes trabajo",
        render: (r) => {
          const p = String(r.period || "");
          if (p.length < 7) return p || "—";
          const m = Number(p.slice(5, 7));
          return `${MES_FULL[m - 1] || p} ${p.slice(0, 4)}`;
        },
      });
    }
    if (canViewAmounts) {
      cols.push({
        key: "expected_amount",
        label: "Importe",
        render: (r) => formatMoney(r.expected_amount, true),
      });
    }
    if (!showEmailUi) {
      cols.push(
        {
          key: "fiscal_status",
          label: "SAT",
          render: (r) => (
            <DespachoStatusBadge variant="secondary">
              {FISCAL_STATUS_LABELS[r.fiscal_status] || r.fiscal_status}
            </DespachoStatusBadge>
          ),
        },
        {
          key: "sat_collected",
          label: "Cobro sistema",
          render: (r) => (
            <Checkbox checked={Boolean(r.sat_collected)} disabled aria-label="Cobro detectado por sistema" />
          ),
        },
        {
          key: "manual_collected",
          label: "Cobrada",
          render: (r) => (
            <Checkbox
              checked={Boolean(r.manual_collected)}
              disabled={readOnly || rowBusyId === r.id}
              onCheckedChange={(checked) => handleToggleManual(r, checked === true)}
              aria-label="Marcar como cobrada manualmente"
            />
          ),
        },
      );
    }
    if (showEmailUi) {
      cols.push({
        key: "collection_email_status",
        label: "Estado envío",
        render: (r) => (
          COLLECTION_EMAIL_STATUS_LABELS[r.collection_email_status]
          || (r.needs_collection_email ? "Sin enviar" : "—")
        ),
      });
    }
    cols.push({
      key: "_actions",
      label: "Acción",
      render: (r) => (
        <div className="flex flex-wrap gap-1">
          {showEmailUi && canGenerateMessages && !readOnly && (r.needs_collection_email ?? r.needs_reminder) ? (
            <Button
              type="button"
              variant="default"
              size="sm"
              className="h-7 bg-[#0F2D5C] hover:bg-[#0a2248]"
              disabled={rowBusyId === r.id || emailBulkBusy || !(r.email_ready || r.client_email)}
              onClick={() => handleSendCollectionEmail(r)}
              data-testid={`honorarios-send-email-${r.id}`}
            >
              <Mail className="h-3.5 w-3.5 mr-1" aria-hidden />
              Enviar correo
            </Button>
          ) : null}
          <Button type="button" variant="ghost" size="sm" className="h-7" onClick={() => openDetail(r.id)}>
            Ver
          </Button>
        </div>
      ),
    });
    return cols;
  }, [
    canViewAmounts,
    isCortesias,
    readOnly,
    rowBusyId,
    canGenerateMessages,
    messageTemplates,
    activeChip,
    selectedRowIds,
    allUnpaidSelected,
    selectableUnpaidRows,
    emailBulkBusy,
  ]);

  const detailItem = detailData?.item;
  const sectionTitle = `Cortesías — ${MES_FULL[Number(periodMonth) - 1]} ${periodYear}`;

  return (
    <div data-testid={isCortesias ? "despacho-honorarios-cortesias" : "despacho-honorarios-monthly-board"}>
      <div className="flex flex-wrap gap-2 mb-3 items-end">
        {!readOnly && canSync && !isCortesias ? (
          <>
            <div>
              <Label htmlFor="honorarios-date-filter-mode" className="text-xs text-slate-600">
                Validación
              </Label>
              <select
                id="honorarios-date-filter-mode"
                className="text-sm border border-slate-200 rounded-sm px-2 py-1.5 bg-white h-8 mt-0.5 min-w-[11rem] block"
                value={dateFilterMode}
                onChange={(e) => {
                  const next = e.target.value === DATE_FILTER_MODE_PERIODO
                    ? DATE_FILTER_MODE_PERIODO
                    : DATE_FILTER_MODE_MES;
                  setDateFilterMode(next);
                  if (next === DATE_FILTER_MODE_MES) {
                    onSyncDateFromChange?.(monthDateFrom);
                    onSyncDateToChange?.(monthDateTo);
                  } else {
                    const y = Number(periodYear) || new Date().getFullYear();
                    onSyncDateFromChange?.(`${y}-01-01`);
                    onSyncDateToChange?.(monthDateTo);
                  }
                }}
                data-testid="honorarios-date-filter-mode"
                aria-label="Modo de validación de fechas"
              >
                <option value={DATE_FILTER_MODE_MES}>Mes de trabajo</option>
                <option value={DATE_FILTER_MODE_PERIODO}>Periodo (desde–hasta)</option>
              </select>
            </div>
            {dateFilterMode === DATE_FILTER_MODE_PERIODO ? (
              <>
                <div>
                  <Label htmlFor="honorarios-sync-desde" className="text-xs text-slate-600">
                    Desde (día/mes/año)
                  </Label>
                  <Input
                    id="honorarios-sync-desde"
                    type="text"
                    inputMode="numeric"
                    placeholder="dd/mm/aaaa"
                    value={
                      /^\d{4}-\d{2}-\d{2}$/.test(syncDateFrom || "")
                        ? isoToDmy(syncDateFrom)
                        : (syncDateFrom || "")
                    }
                    onChange={(e) => {
                      const typed = e.target.value;
                      const iso = dmyToIso(typed);
                      if (iso) onSyncDateFromChange?.(iso);
                      else onSyncDateFromChange?.(typed);
                    }}
                    onBlur={(e) => {
                      const iso = dmyToIso(e.target.value);
                      if (iso) onSyncDateFromChange?.(iso);
                    }}
                    className="rounded-sm h-8 text-sm mt-0.5 w-40"
                    lang="es-MX"
                  />
                </div>
                <div>
                  <Label htmlFor="honorarios-sync-hasta" className="text-xs text-slate-600">
                    Hasta (día/mes/año)
                  </Label>
                  <Input
                    id="honorarios-sync-hasta"
                    type="text"
                    inputMode="numeric"
                    placeholder="dd/mm/aaaa"
                    value={
                      /^\d{4}-\d{2}-\d{2}$/.test(effectiveDateTo || "")
                        ? isoToDmy(effectiveDateTo)
                        : (effectiveDateTo || "")
                    }
                    onChange={(e) => {
                      const typed = e.target.value;
                      const iso = dmyToIso(typed);
                      if (iso) onSyncDateToChange?.(iso);
                      else onSyncDateToChange?.(typed);
                    }}
                    onBlur={(e) => {
                      const iso = dmyToIso(e.target.value);
                      if (iso) onSyncDateToChange?.(iso);
                    }}
                    className="rounded-sm h-8 text-sm mt-0.5 w-40"
                    lang="es-MX"
                  />
                </div>
              </>
            ) : null}
            <div>
              <Label className="text-xs text-slate-600">
                {dateFilterMode === DATE_FILTER_MODE_PERIODO ? "Mes del tablero" : "Mes de trabajo"}
              </Label>
              <div className="flex gap-1 mt-0.5">
                <select
                  className="text-sm border border-slate-200 rounded-sm px-2 py-1.5 bg-white h-8 min-w-[7rem]"
                  value={periodMonth}
                  onChange={(e) => setPeriodMonth(e.target.value)}
                  aria-label={dateFilterMode === DATE_FILTER_MODE_PERIODO ? "Mes del tablero" : "Mes de trabajo"}
                  data-testid="honorarios-work-month"
                >
                  {MES_FULL.map((label, idx) => (
                    <option key={label} value={String(idx + 1).padStart(2, "0")}>{label}</option>
                  ))}
                </select>
                <select
                  className="text-sm border border-slate-200 rounded-sm px-2 py-1.5 bg-white h-8 w-[5.5rem]"
                  value={periodYear}
                  onChange={(e) => setPeriodYear(e.target.value)}
                  aria-label="Año del mes"
                  data-testid="honorarios-work-year"
                >
                  {yearOptions.map((y) => (
                    <option key={y} value={String(y)}>{y}</option>
                  ))}
                </select>
              </div>
              {dateFilterMode === DATE_FILTER_MODE_MES ? (
                <p className="text-[10px] text-slate-500 m-0 mt-0.5" data-testid="honorarios-month-range-hint">
                  Rango: {isoToDmy(monthDateFrom)} – {isoToDmy(monthDateTo)}
                </p>
              ) : (
                <p className="text-[10px] text-slate-500 m-0 mt-0.5">
                  Tablero y «Generar cobranza» usan este mes; sync/reportes usan el periodo.
                </p>
              )}
            </div>
            <div>
              <Label className="text-xs text-slate-600">Sincronización CFDI</Label>
              <div className="flex flex-wrap gap-1 mt-0.5">
                <Button
                  type="button"
                  size="sm"
                  className="rounded-sm h-8 bg-[#0F2D5C] hover:bg-[#0a2248]"
                  disabled={boardBusy || syncBusy}
                  onClick={async () => {
                    await onSyncInvoices?.({
                      period,
                      issue_date_from: rangeDateFrom || undefined,
                      issue_date_to: rangeDateTo || undefined,
                    });
                    await loadConditionCounts();
                    await loadBoard();
                  }}
                  data-testid="despacho-honorarios-sync-resumen"
                >
                  {syncBusy ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5 mr-1" aria-hidden />
                  )}
                  Sincronizar CFDI
                </Button>
              </div>
            </div>
            <Button type="button" size="sm" variant="ghost" className="rounded-sm h-8 text-xs text-slate-600" disabled={boardBusy} onClick={handleRefreshPayments}>
              Solo recalcular cobros
            </Button>
            <Button type="button" size="sm" className="rounded-sm h-8 bg-[#0F2D5C] hover:bg-[#0a2248]" disabled={boardBusy} onClick={handleGenerateMonth}>
              {boardBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden /> : "Generar cobranza del mes"}
            </Button>
          </>
        ) : (
          <div>
            <Label className="text-xs text-slate-600">Mes de trabajo</Label>
            <div className="flex gap-1 mt-0.5">
              <select
                className="text-sm border border-slate-200 rounded-sm px-2 py-1.5 bg-white h-8 min-w-[7rem]"
                value={periodMonth}
                onChange={(e) => setPeriodMonth(e.target.value)}
              >
                {MES_FULL.map((label, idx) => (
                  <option key={label} value={String(idx + 1).padStart(2, "0")}>{label}</option>
                ))}
              </select>
              <select
                className="text-sm border border-slate-200 rounded-sm px-2 py-1.5 bg-white h-8 w-[5.5rem]"
                value={periodYear}
                onChange={(e) => setPeriodYear(e.target.value)}
              >
                {yearOptions.map((y) => (
                  <option key={y} value={String(y)}>{y}</option>
                ))}
              </select>
            </div>
          </div>
        )}
        {canExport ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                type="button"
                size="sm"
                variant="outline"
                className="rounded-sm h-8"
                disabled={exportBusy}
                data-testid="despacho-honorarios-export-menu"
              >
                {exportBusy ? (
                  <Loader2 className="h-3.5 w-3.5 mr-1 animate-spin" aria-hidden />
                ) : (
                  <Download className="h-3.5 w-3.5 mr-1" aria-hidden />
                )}
                Reportes
                <ChevronDown className="h-3.5 w-3.5 ml-1 opacity-70" aria-hidden />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="min-w-[14rem]">
              <DropdownMenuItem
                disabled={exportBusy}
                onClick={() => handleExport("pending")}
                data-testid="honorarios-export-por-cobrar"
              >
                Facturas por cobrar (Excel)
              </DropdownMenuItem>
              <DropdownMenuItem
                disabled={exportBusy}
                onClick={() => handleExport("collected")}
                data-testid="honorarios-export-cobradas"
              >
                Facturas cobradas (Excel)
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              <DropdownMenuItem
                disabled={exportBusy}
                onClick={() => handleExport("view")}
              >
                Exportar vista actual (CSV)
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>

      {isCortesias ? (
        <>
          {loadState === "loading" ? (
            <div className="flex items-center gap-2 text-sm text-slate-600 py-4">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Cargando tablero…
            </div>
          ) : null}
          {loadState === "ready" ? (
            <DespachoSectionCard title={sectionTitle}>
              <DespachoSimpleTable
                columns={columns}
                rows={rows.map((r) => ({ ...r, id: r.id }))}
                emptyLabel="Sin cortesías en este mes."
              />
            </DespachoSectionCard>
          ) : null}
        </>
      ) : (
        <div className="space-y-3" data-testid="honorarios-condition-tabs">
          <p className="text-xs text-slate-500 m-0">
            Cada tarjeta es una <span className="font-medium text-slate-700">condición</span> (factura, convenio, cortesía…).
            Abra una para ver la cobranza del mes. Para reclasificar un cliente: «Ver» → «Condición / situación».
            También puede cambiarla en Lista de clientes (lápiz).
          </p>
          {CONDITION_CLIENT_TABS.map((tab) => {
            const isOpen = openConditionKey === tab.key;
            return (
              <ConditionAccordionSection
                key={tab.key}
                title={tab.label}
                description={tab.description}
                count={conditionCounts[tab.key]}
                isOpen={isOpen}
                onToggle={() => toggleConditionAccordion(tab.key)}
                testId={`honorarios-condition-tab-${tab.key}`}
              >
                {tab.billing_condition !== "cortesia" ? (
                  <div className="flex flex-wrap gap-1.5">
                    {COBRANZA_CHIPS.map((chip) => (
                      <Button
                        key={chip.key}
                        type="button"
                        size="sm"
                        variant={activeChip === chip.key ? "default" : "outline"}
                        className={`rounded-sm h-7 text-xs ${activeChip === chip.key ? "bg-[#0F2D5C] hover:bg-[#0a2248]" : ""}`}
                        onClick={() => setActiveChip(chip.key)}
                      >
                        {chip.label}
                      </Button>
                    ))}
                  </div>
                ) : null}

                {isOpen && (activeChip === "pending" || activeChip === "email") && tab.billing_condition !== "cortesia" && pendingRangeLabel ? (
                  <p className="text-xs text-slate-600 m-0">{pendingRangeLabel}</p>
                ) : null}

                {isOpen && loadState === "ready" && activeChip !== "email" ? (
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                    <DespachoKpiCard
                      label="Pendiente real"
                      value={canViewAmounts ? formatMoney(summary.pending_real_amount ?? summary.pending_amount, true) : (summary.pending_count ?? 0)}
                      tone="warn"
                    />
                    <DespachoKpiCard
                      label="Cobrado SAT"
                      value={canViewAmounts ? formatMoney(summary.sat_collected_amount, true) : "—"}
                      tone="info"
                    />
                    <DespachoKpiCard
                      label="Cobrado manual"
                      value={canViewAmounts ? formatMoney(summary.manual_collected_amount, true) : "—"}
                      tone="info"
                    />
                    <DespachoKpiCard label="Recordatorios" value={summary.reminder_pending_count ?? 0} tone="warn" />
                  </div>
                ) : null}

                {isOpen && loadState === "ready" && activeChip === "email" && tab.billing_condition !== "cortesia" ? (
                  <div className="space-y-3" data-testid="honorarios-email-send-section">
                    <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                      <DespachoKpiCard label="No cobrados" value={emailValidation.unpaidCount} tone="warn" />
                      <DespachoKpiCard label="Con correo" value={emailValidation.readyCount} tone="info" />
                      <DespachoKpiCard label="Sin correo" value={emailValidation.missingCount} tone="warn" />
                      <DespachoKpiCard label="Ya enviados" value={emailValidation.alreadySentCount} tone="info" />
                    </div>
                    <div
                      className={`rounded-sm border px-3 py-2 text-xs ${
                        emailValidation.transportReady
                          ? "border-emerald-200 bg-emerald-50 text-emerald-900"
                          : "border-amber-200 bg-amber-50 text-amber-900"
                      }`}
                      data-testid="honorarios-email-transport-status"
                    >
                      {emailConfigState === "loading" ? (
                        <span>Validando Parámetros de correo…</span>
                      ) : emailConfigState === "error" ? (
                        <span>No se pudo leer la configuración de correo. Revise Calendario → Parámetros de correo.</span>
                      ) : emailValidation.transportReady ? (
                        <span>
                          Correo listo para enviar
                          {emailConfig?.from_email ? ` · Remitente: ${emailConfig.from_email}` : ""}.
                          Solo se incluyen filas con «Cobrada» sin marcar.
                        </span>
                      ) : (
                        <span>
                          {emailValidation.transportHint
                            || "Active «Permitir envíos» en Parámetros de correo (Calendario) antes de enviar."}
                        </span>
                      )}
                    </div>

                    <div
                      className="rounded-sm border border-slate-200 bg-slate-50 p-3 space-y-2"
                      data-testid="honorarios-email-template-panel"
                    >
                      <p className="text-xs font-medium text-[#0F2D5C] m-0">
                        Plantilla predeterminada
                      </p>
                      <p className="text-[11px] text-slate-500 m-0">
                        El destinatario (nombre y correo) se toma de la ficha del cliente.
                        Variables: {"{{cliente}}"}, {"{{periodo}}"}, {"{{folio}}"}, {"{{total}}"}, {"{{rfc}}"}.
                      </p>
                      {mailTplState === "loading" ? (
                        <p className="text-xs text-slate-500 m-0">Cargando plantilla…</p>
                      ) : null}
                      <div>
                        <Label htmlFor="honorarios-mail-subject" className="text-xs">Asunto</Label>
                        <Input
                          id="honorarios-mail-subject"
                          className="mt-1 h-8 text-xs"
                          value={mailTpl.subject}
                          disabled={readOnly || !canGenerateMessages || mailTplSaving}
                          onChange={(e) => setMailTpl((prev) => ({ ...prev, subject: e.target.value }))}
                          placeholder="Recordatorio de honorarios {{periodo}} — {{folio}}"
                          data-testid="honorarios-email-template-subject"
                        />
                      </div>
                      <div>
                        <Label htmlFor="honorarios-mail-body" className="text-xs">Texto del mensaje</Label>
                        <textarea
                          id="honorarios-mail-body"
                          className="mt-1 w-full min-h-[120px] text-xs border border-slate-200 rounded-sm px-2 py-1.5 bg-white"
                          value={mailTpl.body}
                          disabled={readOnly || !canGenerateMessages || mailTplSaving}
                          onChange={(e) => setMailTpl((prev) => ({ ...prev, body: e.target.value }))}
                          data-testid="honorarios-email-template-body"
                        />
                      </div>
                      <div className="flex flex-wrap gap-4 text-xs text-slate-700">
                        <label className="inline-flex items-center gap-1.5">
                          <Checkbox
                            checked={Boolean(mailTpl.attach_cfdi_pdf)}
                            disabled={readOnly || !canGenerateMessages || mailTplSaving}
                            onCheckedChange={(v) => setMailTpl((prev) => ({ ...prev, attach_cfdi_pdf: Boolean(v) }))}
                            data-testid="honorarios-email-attach-pdf"
                          />
                          Adjuntar PDF de la factura
                        </label>
                        <label className="inline-flex items-center gap-1.5">
                          <Checkbox
                            checked={Boolean(mailTpl.attach_cfdi_xml)}
                            disabled={readOnly || !canGenerateMessages || mailTplSaving}
                            onCheckedChange={(v) => setMailTpl((prev) => ({ ...prev, attach_cfdi_xml: Boolean(v) }))}
                            data-testid="honorarios-email-attach-xml"
                          />
                          Adjuntar XML del CFDI
                        </label>
                      </div>
                      {canGenerateMessages && !readOnly ? (
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs rounded-sm"
                          disabled={mailTplSaving}
                          onClick={handleSaveMailTemplate}
                          data-testid="honorarios-email-template-save"
                        >
                          {mailTplSaving ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : null}
                          Guardar plantilla
                        </Button>
                      ) : null}
                    </div>

                    {canGenerateMessages && !readOnly ? (
                      <div className="flex flex-wrap gap-2 items-center" data-testid="honorarios-collection-email-bar">
                        <Button
                          type="button"
                          size="sm"
                          className="h-7 text-xs rounded-sm bg-[#0F2D5C] hover:bg-[#0a2248]"
                          disabled={
                            emailBulkBusy
                            || !selectedRowIds.size
                            || !emailValidation.transportReady
                          }
                          onClick={() => handleBulkCollectionEmails(true)}
                          data-testid="honorarios-email-bulk-selected"
                        >
                          {emailBulkBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <Mail className="h-3.5 w-3.5 mr-1" />}
                          Enviar seleccionados ({selectedRowIds.size})
                        </Button>
                        <Button
                          type="button"
                          size="sm"
                          variant="outline"
                          className="h-7 text-xs rounded-sm"
                          disabled={
                            emailBulkBusy
                            || !emailValidation.readyCount
                            || !emailValidation.transportReady
                          }
                          onClick={() => handleBulkCollectionEmails(false)}
                          data-testid="honorarios-email-bulk-unpaid"
                        >
                          Enviar a todos con correo ({emailValidation.readyCount})
                        </Button>
                        <span className="text-[10px] text-slate-500">
                          Valida correo en ficha y envía solo a no cobrados.
                        </span>
                      </div>
                    ) : null}
                  </div>
                ) : null}

                {isOpen && loadState === "loading" ? (
                  <div className="flex items-center gap-2 text-sm text-slate-600 py-2">
                    <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
                    Cargando tablero…
                  </div>
                ) : null}

                {isOpen && loadState === "ready" && canGenerateMessages && !readOnly && activeChip === "reminder" && tab.billing_condition !== "cortesia" ? (
                  <div className="flex flex-wrap gap-2 items-center" data-testid="honorarios-reminder-bulk-bar">
                    <Button
                      type="button"
                      size="sm"
                      variant="outline"
                      className="h-7 text-xs rounded-sm"
                      disabled={reminderBulkBusy || !selectedRowIds.size}
                      onClick={() => handleBulkReminders(true)}
                      data-testid="honorarios-reminder-bulk-selected"
                    >
                      {reminderBulkBusy ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" /> : <MessageSquare className="h-3.5 w-3.5 mr-1" />}
                      Preparar WhatsApp ({selectedRowIds.size})
                    </Button>
                    <span className="text-[10px] text-slate-400">
                      Borradores para WhatsApp (futuro). No envía correo.
                    </span>
                  </div>
                ) : null}

                {isOpen && loadState === "ready" ? (
                  <DespachoSimpleTable
                    columns={columns}
                    rows={rows.map((r) => ({ ...r, id: r.id }))}
                    emptyLabel={
                      activeChip === "email"
                        ? "No hay clientes no cobrados en este rango. Marque «Cobrada» solo cuando paguen."
                        : `Sin ${tab.label.toLowerCase()} en este filtro. Genere cobranza del mes o sincronice CFDI.`
                    }
                  />
                ) : null}
              </ConditionAccordionSection>
            );
          })}
        </div>
      )}

      <Dialog open={detailOpen} onOpenChange={setDetailOpen}>
        <DialogContent className="max-w-lg rounded-sm max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{detailItem?.client_name || "Detalle cobranza"}</DialogTitle>
          </DialogHeader>
          {detailBusy ? (
            <div className="flex items-center gap-2 text-sm py-4">
              <Loader2 className="h-4 w-4 animate-spin" aria-hidden />
              Cargando…
            </div>
          ) : null}
          {!detailBusy && detailItem ? (
            <div className="space-y-3 text-sm">
              <p><span className="text-slate-500">RFC:</span> {detailItem.receiver_rfc || "—"}</p>
              {!readOnly ? (
                <div className="rounded-sm border border-slate-200 bg-slate-50 p-2 space-y-2" data-testid="honorarios-detail-condition">
                  <Label htmlFor="detail-billing-condition">
                    Condición / situación (factura, convenio, cortesía…)
                  </Label>
                  <select
                    id="detail-billing-condition"
                    className="w-full text-sm border border-slate-200 rounded-sm px-2 py-1.5 bg-white"
                    value={conditionDraft || detailItem.billing_condition || ""}
                    onChange={(e) => setConditionDraft(e.target.value)}
                    data-testid="honorarios-detail-condition-select"
                  >
                    {CONDITION_EDIT_OPTIONS.map((opt) => (
                      <option key={opt.value} value={opt.value}>{opt.label}</option>
                    ))}
                  </select>
                  <div>
                    <Label htmlFor="detail-condition-comment">Comentario del cambio</Label>
                    <Input
                      id="detail-condition-comment"
                      className="mt-1 h-8 text-xs"
                      value={patchComment}
                      onChange={(e) => setPatchComment(e.target.value)}
                      placeholder="Motivo del cambio de condición…"
                    />
                  </div>
                  <Button
                    type="button"
                    size="sm"
                    className="h-8 text-xs bg-[#0F2D5C]"
                    onClick={handleChangeCondition}
                    disabled={!conditionDraft || conditionDraft === detailItem.billing_condition}
                    data-testid="honorarios-detail-save-condition"
                  >
                    Cambiar condición
                  </Button>
                </div>
              ) : (
                <p>
                  <span className="text-slate-500">Condición:</span>{" "}
                  {BILLING_CONDITION_LABELS[detailItem.billing_condition] || detailItem.billing_condition}
                </p>
              )}
              <p>
                <span className="text-slate-500">Estado SAT:</span>{" "}
                {FISCAL_STATUS_LABELS[detailItem.fiscal_status] || detailItem.fiscal_status}
              </p>
              <div>
                <Label htmlFor="detail-reminder">Recordatorio</Label>
                <select
                  id="detail-reminder"
                  className="w-full mt-1 text-sm border border-slate-200 rounded-sm px-2 py-2"
                  value={detailItem.reminder_status || "pending"}
                  disabled={readOnly}
                  onChange={(e) => handlePatchRow({ reminder_status: e.target.value })}
                >
                  {Object.entries(REMINDER_STATUS_LABELS).map(([k, label]) => (
                    <option key={k} value={k}>{label}</option>
                  ))}
                </select>
              </div>
              {detailData?.invoice ? (
                <div className="rounded-sm border border-slate-200 p-2 space-y-2" data-testid="honorarios-detail-cfdi">
                  <p className="font-medium">Factura CFDI vinculada</p>
                  <p>Fecha factura: {formatIssueDate(detailItem.invoice_issue_date || detailData.invoice.issue_date)}</p>
                  <p>Serie/Folio: {detailData.invoice.invoice_label || [detailData.invoice.serie, detailData.invoice.folio].filter(Boolean).join("-")}</p>
                  <p>UUID: {detailData.invoice.cfdi_uuid || "—"}</p>
                  <p>
                    Estado fiscal:{" "}
                    {collectionStatusLabel(detailData.invoice.collection_status)
                      || detailData.invoice.collection_status
                      || "—"}
                  </p>
                  {canViewAmounts ? <p>Total: {formatMoney(detailData.invoice.total, true)}</p> : null}
                  <div className="flex flex-wrap gap-1 pt-1">
                    {detailData.invoice.cfdi_uuid ? (
                      <Button
                        type="button"
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs rounded-sm"
                        onClick={handleOpenCfdiVisor}
                        data-testid="honorarios-detail-visor-cfdi"
                      >
                        <ExternalLink className="h-3.5 w-3.5 mr-1" aria-hidden />
                        Ver en visor SAT
                      </Button>
                    ) : null}
                    {canConfirmPayment && !readOnly && detailData.invoice.collection_status !== "paid_confirmed" ? (
                      <Button
                        type="button"
                        size="sm"
                        className="h-7 text-xs rounded-sm bg-[#0F2D5C]"
                        onClick={handleConfirmFiscalPaid}
                        data-testid="honorarios-detail-confirm-paid"
                      >
                        Confirmar pago fiscal
                      </Button>
                    ) : null}
                  </div>
                  {canCreateHonorariosTasks && !readOnly && taskKinds.length ? (
                    <div className="flex flex-wrap gap-1 pt-1">
                      {taskKinds.slice(0, 4).map((k) => (
                        <Button
                          key={k.id}
                          type="button"
                          size="sm"
                          variant="ghost"
                          className="h-7 text-xs rounded-sm"
                          disabled={taskBusy}
                          onClick={() => handleCreateTaskFromDetail(k.id)}
                        >
                          {k.label}
                        </Button>
                      ))}
                    </div>
                  ) : null}
                  {!readOnly ? (
                    <form onSubmit={handleUploadProofFromDetail} className="space-y-2 pt-2 border-t border-slate-100">
                      <p className="text-xs font-medium text-slate-700 m-0">Comprobante de pago</p>
                      <Input
                        type="file"
                        accept=".pdf,.png,.jpg,.jpeg"
                        className="rounded-sm h-8 text-xs"
                        onChange={(e) => setProofFile(e.target.files?.[0] || null)}
                      />
                      <Button
                        type="submit"
                        size="sm"
                        variant="outline"
                        className="h-7 text-xs rounded-sm"
                        disabled={proofBusy || !proofFile}
                      >
                        {proofBusy
                          ? <Loader2 className="h-3.5 w-3.5 animate-spin mr-1" aria-hidden />
                          : <FileUp className="h-3.5 w-3.5 mr-1" aria-hidden />}
                        Subir comprobante
                      </Button>
                    </form>
                  ) : null}
                </div>
              ) : (
                <p className="text-xs text-slate-500">
                  Sin factura CFDI vinculada. Sincronice CFDI o genere la cobranza del mes.
                </p>
              )}
              <p className="text-[10px] text-slate-400 m-0">
                El checkbox «Cobrada» del tablero confirma el cobro operativo
                {canConfirmPayment ? " y, si hay CFDI, el estado fiscal de la factura." : " (el auxiliar reporta pago; la confirmación fiscal la hace el titular)."}
              </p>
              <div>
                <Label htmlFor="detail-collection-date">Fecha de cobro</Label>
                <Input
                  id="detail-collection-date"
                  type="date"
                  className="rounded-sm mt-1"
                  value={detailItem.collection_date ? String(detailItem.collection_date).slice(0, 10) : ""}
                  disabled={readOnly}
                  onChange={(e) => handlePatchRow({ collection_date: e.target.value })}
                />
              </div>
              <div>
                <Label htmlFor="detail-notes">Notas</Label>
                <Input
                  id="detail-notes"
                  className="rounded-sm mt-1"
                  defaultValue={detailItem.notes || ""}
                  disabled={readOnly}
                  onBlur={(e) => {
                    if (e.target.value !== (detailItem.notes || "")) {
                      handlePatchRow({ notes: e.target.value });
                    }
                  }}
                />
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>
    </div>
  );
}
